import React, { useState, useEffect } from "react";
import { scroller } from "react-scroll";
import Headroom from "react-headroom";
import { useTranslation } from "react-i18next";
import LanguageDropdown from "components/LanguageDropdown";

// Header, mobile menu and footer shared by every public page, so the payment,
// receipt and error screens read as one site.
const PublicLayout = ({ children }) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { t } = useTranslation();

  const scrollTo = (event, target) => {
    event.preventDefault();
    scroller.scrollTo(target, {
      duration: 500,
      delay: 0,
      smooth: "easeInOutQuart",
      offset: -100,
    });
    return false;
  };

  useEffect(() => {
    const closeMenu = () => setShowMobileMenu(false);
    const closeOnEscape = (event) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("scroll", closeMenu);
    window.addEventListener("click", closeMenu);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("scroll", closeMenu);
      window.removeEventListener("click", closeMenu);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <div className={`landing-page ${showMobileMenu ? "show-mobile-menu" : ""}`}>
      <div className="main-container">
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <div
          className="mobile-menu"
          id="mobile-menu"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="col-12 d-block mt-3 text-center">
            <img
              src="/static/img/vertical.png"
              alt="Edu-Action"
              className="logo-white"
            />
          </div>
          <ul className="navbar-nav">
            <li className="nav-item">
              <a
                href="https://examscambridge.uz/exam-dates/"
                target="_blank"
                rel="noopener noreferrer"
              >
                TKT {t("examDates")}
              </a>
            </li>
            <li className="nav-item">
              <div className="separator" />
            </li>
            <li className="nav-item">
              <h4 className="text-center">{t("results")}</h4>
            </li>
            <li className="nav-item">
              <a
                href="https://results.edu-action.uk/"
                target="_blank"
                rel="noopener noreferrer"
              >
                TKT {t("results")}
              </a>
            </li>
            <li className="nav-item">
              <a
                href="https://www.metritests.com/metrica/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Linguaskill {t("results")}
              </a>
            </li>
            <li className="nav-item">
              <div className="separator" />
            </li>
            <li className="nav-item">
              <a
                href="https://sway.office.com/gjgMU9QqCLkj6VS9?ref=email"
                target="_blank"
                rel="noopener noreferrer"
              >
                CELTA
              </a>
            </li>
            <li className="nav-item">
              <div className="separator" />
            </li>
            <li className="nav-item">
              <a href="#contactUs" onClick={() => setShowMobileMenu(false)}>
                {t("contacts")}
              </a>
            </li>
          </ul>
        </div>

        <Headroom className="landing-page-nav">
          <nav>
            <div className="container d-flex align-items-center justify-content-end">
              <div className="d-flex flex-row align-items-center">
                <LanguageDropdown />
                <a
                  className="mobile-menu-button mr-2"
                  href="https://t.me/Edu_Exams"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Telegram"
                  title="Telegram"
                >
                  <i className="simple-icon-paper-plane" aria-hidden="true" />
                </a>
                <a
                  className="mobile-menu-button mr-2"
                  href="https://instagram.com/edu_exams"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  title="Instagram"
                >
                  <i
                    className="simple-icon-social-instagram"
                    aria-hidden="true"
                  />
                </a>
                <button
                  type="button"
                  className="mobile-menu-button btn-reset"
                  aria-label={t("menu")}
                  aria-expanded={showMobileMenu}
                  aria-controls="mobile-menu"
                  onClick={(event) => {
                    setShowMobileMenu(!showMobileMenu);
                    event.stopPropagation();
                  }}
                >
                  <i className="simple-icon-menu" aria-hidden="true" />
                </button>
              </div>
            </div>
          </nav>
        </Headroom>

        <div className="content-container" id="home">
          <div className="section home">
            <div className="container">
              <div className="row home-row pt-2">
                <div className="col-12 d-block mt-3 text-center">
                  <img
                    src="/static/img/EA-vertical-white.png"
                    alt="Edu-Action"
                    className="logo-white"
                  />
                </div>
                {children}
              </div>
            </div>
          </div>

          <footer className="section footer mb-0">
            <div className="container" style={{ zIndex: 0 }}>
              <div className="row footer-row">
                <div className="col-12 text-right">
                  <a
                    className="btn btn-circle btn-outline-semi-light footer-circle-button"
                    href="#home"
                    aria-label={t("backToTop")}
                    title={t("backToTop")}
                    onClick={(event) => scrollTo(event, "home")}
                  >
                    <i className="simple-icon-arrow-up" aria-hidden="true" />
                  </a>
                </div>
                <div className="col-12 text-center footer-content">
                  <div className="col-12 d-block mt-3 text-center">
                    <img
                      src="/static/img/EA-vertical-white.png"
                      alt="Edu-Action"
                      className="logo-white"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="container copyright pt-5 pb-5" id="contactUs">
              <div className="row">
                <div className="col-12 text-center">
                  <p className="mb-0">
                    {t("telephone")}:{" "}
                    <a className="text-white" href="tel:+998712300103">
                      +998 71 230 01 03
                    </a>
                  </p>
                  <p className="mb-0">
                    <a className="text-white" href="https://t.me/tktonlinebot">
                      Telegram bot: @tktonlinebot
                    </a>
                  </p>
                  <p className="mb-0">
                    {t("address1")}
                    <br />
                    {t("address2")}
                  </p>
                  <p className="mb-0 mt-4">
                    © {new Date().getFullYear()} Edu-Action
                  </p>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};

export default PublicLayout;
